import { useNavigate } from "react-router-dom";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import MfaEnforcementGate from "@/components/auth/MfaEnforcementGate";

type Props = { children: React.ReactNode; roles?: string[] };

export function ProtectedRoute({ children, roles }: Props) {
  const navigate = useNavigate();
  const [hasSession, setHasSession] = useState<boolean | null>(null);
  const { role, loading } = useEffectiveRole();

  useEffect(() => {
    supabase.auth.getSession().then(({ data, error }) => {
      if (error || !data?.session) {
        setHasSession(false);
      } else {
        setHasSession(true);
      }
    });
  }, []);

  // Redirect to login if no session (imperative, not <Navigate />)
  useEffect(() => {
    if (hasSession === false) {
      navigate("/login", { replace: true });
    }
  }, [hasSession, navigate]);
  
  if (hasSession === null || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (hasSession === false) {
    // Will redirect via useEffect, show nothing
    return null;
  }

  if (!role) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-amber-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-2">No Role Found</h2>
          <p className="text-muted-foreground">No role found for your account. Contact your admin.</p>
        </div>
      </div>
    );
  }

  const allowed = !roles || roles.some(r => r.toLowerCase() === role.toLowerCase());
  
  if (!allowed) {
    console.warn('ProtectedRoute: Access denied', { 
      requiredRoles: roles, 
      userRole: role
    });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-destructive text-5xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-4">
            You don't have the required permissions to access this page.
          </p>
          <p className="text-sm text-muted-foreground">
            Required roles: {roles?.join(', ')}
          </p>
        </div>
      </div>
    );
  }
  
  return <MfaEnforcementGate>{children}</MfaEnforcementGate>;
}